/**
 * HUB-B.3 — Liste ventes partagée (table desktop + cartes mobile + pagination).
 */
import React from 'react';
import { ChevronLeft, ChevronRight, User, Banknote, CreditCard } from 'lucide-react';
import MagasinBadge from '../MagasinBadge';
import HistoriqueVenteCard from './HistoriqueVenteCard';
import HistoriqueVenteActionsMenu from './HistoriqueVenteActionsMenu';
import { typeBadgeClass } from '../../utils/historique/ventesDisplay';

const HISTORIQUE_LEGACY_TEST_IDS = {
  tableDesktop: 'historique-table-desktop',
  mobileCards: 'historique-mobile-cards',
  emptyDesktop: 'historique-empty-desktop',
  emptyMobile: 'historique-empty-mobile',
  rowPrefix: 'historique-row',
  pagination: 'historique-pagination',
  prev: 'historique-page-prev',
  next: 'historique-page-next',
};

function resolveListTestId(testIdPrefix, key, fallback) {
  if (testIdPrefix === 'historique' && HISTORIQUE_LEGACY_TEST_IDS[key]) {
    return HISTORIQUE_LEGACY_TEST_IDS[key];
  }
  return fallback;
}

export default function HistoriqueVentesList({
  ventes,
  loading,
  isAdmin,
  currentPage,
  totalPages,
  onPageChange,
  convertingRef,
  actionHandlers,
  testIdPrefix = 'historique',
  emptyDesktopLabel = 'Aucune vente',
  emptyMobileLabel = 'Aucune vente trouvée',
}) {
  const tableDesktopId = resolveListTestId(testIdPrefix, 'tableDesktop', `${testIdPrefix}-ventes-table-desktop`);
  const mobileCardsId = resolveListTestId(testIdPrefix, 'mobileCards', `${testIdPrefix}-ventes-mobile-cards`);
  const emptyDesktopId = resolveListTestId(testIdPrefix, 'emptyDesktop', `${testIdPrefix}-ventes-empty-desktop`);
  const emptyMobileId = resolveListTestId(testIdPrefix, 'emptyMobile', `${testIdPrefix}-ventes-empty-mobile`);
  const paginationId = resolveListTestId(testIdPrefix, 'pagination', `${testIdPrefix}-ventes-pagination`);
  const prevId = resolveListTestId(testIdPrefix, 'prev', `${testIdPrefix}-ventes-prev`);
  const nextId = resolveListTestId(testIdPrefix, 'next', `${testIdPrefix}-ventes-next`);
  const rowTestId = (ref) => (
    testIdPrefix === 'historique' ? `historique-row-${ref}` : `${testIdPrefix}-vente-row-${ref}`
  );

  return (
    <>
      <div className="hidden lg:block overflow-x-auto" data-testid={tableDesktopId}>
        <table className="w-full table-dark-custom text-sm">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Magasin</th>
              <th>Type</th>
              <th>Désignation</th>
              <th>Montant</th>
              <th>Paiement</th>
              <th>Vendeur</th>
              <th>Date</th>
              <th className="w-16">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center py-8">
                  <div className="loading-spinner mx-auto" />
                </td>
              </tr>
            ) : ventes.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-12 text-[var(--rm-gray-500)]" data-testid={emptyDesktopId}>
                  {emptyDesktopLabel}
                </td>
              </tr>
            ) : (
              ventes.map((vente) => (
                <tr key={vente.id || vente.ref_vente} data-testid={rowTestId(vente.ref_vente)}>
                  <td className="font-mono text-xs text-[var(--rm-white)]">{vente.ref_vente}</td>
                  <td><MagasinBadge code={vente.magasin_id || 'M1'} size="sm" /></td>
                  <td>
                    <span className={typeBadgeClass(vente.type)}>
                      {vente.type === 'complement' ? 'complément' : (vente.type || 'vente')}
                    </span>
                    {vente.statut_paiement === 'partielle' && (
                      <span className="ml-1 rm-badge rm-badge--danger">
                        Reste {vente.reste_a_payer?.toFixed(2)} €
                      </span>
                    )}
                  </td>
                  <td className="max-w-[200px] truncate">{vente.designation}</td>
                  <td className="font-bold text-[var(--rm-success)]">{vente.prix_total?.toFixed(2)} €</td>
                  <td>
                    <div className="flex items-center gap-1">
                      {vente.mode_paiement?.includes('Espèces') && <Banknote className="w-4 h-4 text-[var(--rm-success)]" />}
                      {(vente.mode_paiement?.includes('CB') || vente.mode_paiement?.includes('Carte')) && (
                        <CreditCard className="w-4 h-4 text-[var(--rm-gray-400)]" />
                      )}
                      <span>{vente.mode_paiement}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4 text-[var(--rm-gray-500)]" />
                      <span>{vente.vendeur}</span>
                    </div>
                  </td>
                  <td className="text-[var(--rm-gray-500)] whitespace-nowrap">
                    {new Date(vente.date_vente).toLocaleString('fr-FR', {
                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <HistoriqueVenteActionsMenu
                      vente={vente}
                      isAdmin={isAdmin}
                      convertingRef={convertingRef}
                      testIdPrefix={testIdPrefix}
                      {...actionHandlers}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden space-y-3" data-testid={mobileCardsId}>
        {loading ? (
          <div className="rounded-xl border p-8 text-center" style={{ borderColor: 'var(--rm-border-subtle)' }}>
            <div className="loading-spinner mx-auto" />
          </div>
        ) : ventes.length === 0 ? (
          <div
            className="rounded-xl border p-12 text-center text-[var(--rm-gray-500)]"
            style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
            data-testid={emptyMobileId}
          >
            {emptyMobileLabel}
          </div>
        ) : (
          ventes.map((vente) => (
            <HistoriqueVenteCard
              key={vente.id || vente.ref_vente}
              vente={vente}
              isAdmin={isAdmin}
              convertingRef={convertingRef}
              actionHandlers={actionHandlers}
              testIdPrefix={testIdPrefix}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div
          className="flex items-center justify-center gap-4 p-4 border-t"
          style={{ borderColor: 'var(--rm-border-subtle)' }}
          data-testid={paginationId}
        >
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="rm-touch rounded-xl border hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            style={{ borderColor: 'var(--rm-border-subtle)' }}
            data-testid={prevId}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-[var(--rm-gray-300)]">
            Page {currentPage} sur {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="rm-touch rounded-xl border hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            style={{ borderColor: 'var(--rm-border-subtle)' }}
            data-testid={nextId}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
}
